import React, { useState, createContext, useContext } from 'react';
import { User, Listing, ListingType, LISTING_PRICES } from '../types';
interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => boolean;
  signup: (name: string, email: string, password: string) => boolean;
  logout: () => void;
  userListings: Listing[];
  createListing: (listing: Omit<Listing, 'id' | 'userId' | 'userName' | 'userAvatar' | 'views' | 'createdAt'>) => {
    success: boolean;
    message: string;
  };
  deleteListing: (id: string) => void;
  addFunds: (amount: number) => void;
}
const AuthContext = createContext<AuthContextType | undefined>(undefined);
const mockUsers: {
  email: string;
  password: string;
  user: User;
}[] = [];
export function AuthProvider({
  children
}: {
  children: ReactNode;
}) {
  const [user, setUser] = useState<User | null>(null);
  const [userListings, setUserListings] = useState<Listing[]>([]);
  const login = (email: string, password: string): boolean => {
    const found = mockUsers.find(u => u.email === email && u.password === password);
    if (found) {
      setUser(found.user);
      return true;
    }
    // Demo login
    if (email && password) {
      const demoUser: User = {
        id: 'demo-user',
        name: email.split('@')[0],
        email,
        avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop',
        walletBalance: 500,
        location: 'Nakuru, Kenya',
        joinedDate: new Date().toISOString()
      };
      setUser(demoUser);
      return true;
    }
    return false;
  };
  const signup = (name: string, email: string, password: string): boolean => {
    if (mockUsers.find(u => u.email === email)) {
      return false;
    }
    const newUser: User = {
      id: `user-${Date.now()}`,
      name,
      email,
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop',
      walletBalance: 500,
      location: 'Nakuru, Kenya',
      joinedDate: new Date().toISOString()
    };
    mockUsers.push({
      email,
      password,
      user: newUser
    });
    setUser(newUser);
    return true;
  };
  const logout = () => {
    setUser(null);
    setUserListings([]);
  };
  const createListing = (listingData: Omit<Listing, 'id' | 'userId' | 'userName' | 'userAvatar' | 'views' | 'createdAt'>): {
    success: boolean;
    message: string;
  } => {
    if (!user) {
      return {
        success: false,
        message: 'You must be logged in to create a listing'
      };
    }
    const price = LISTING_PRICES[listingData.type];
    if (price > 0 && user.walletBalance < price) {
      return {
        success: false,
        message: `Insufficient balance. You need ${price} KES to create this listing.`
      };
    }
    const newListing: Listing = {
      ...listingData,
      id: `listing-${Date.now()}`,
      userId: user.id,
      userName: user.name,
      userAvatar: user.avatar,
      views: 0,
      createdAt: new Date().toISOString()
    };
    if (price > 0) {
      setUser({
        ...user,
        walletBalance: user.walletBalance - price
      });
    }
    setUserListings([...userListings, newListing]);
    return {
      success: true,
      message: price > 0 ? `Listing created! ${price} KES deducted from your wallet.` : 'Listing created successfully!'
    };
  };
  const deleteListing = (id: string) => {
    setUserListings(userListings.filter(l => l.id !== id));
  };
  const addFunds = (amount: number) => {
    if (user) {
      setUser({
        ...user,
        walletBalance: user.walletBalance + amount
      });
    }
  };
  return <AuthContext.Provider value={{
    user,
    isAuthenticated: !!user,
    login,
    signup,
    logout,
    userListings,
    createListing,
    deleteListing,
    addFunds
  }} data-id="element-236">
      {children}
    </AuthContext.Provider>;
}
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}